import React from 'react';
import StarIcon from '@mui/icons-material/Star';
import { Box, CircularProgress, Rating, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { useSalonPublicTheme } from '../../../../context/SalonPublicThemeContext';
import SalonPublicButton from '../../salonPublicAppearance/SalonPublicButton';
import SalonPublicStyleScope from '../../salonPublicAppearance/SalonPublicStyleScope';


const REVIEW_VARIANTS = new Set(['cards', 'quotes', 'editorial', 'compact', 'clinical']);

const SalonTemplateReviews = ({
  data,
  variant = 'cards',
  visible = true,
  mode = 'public',
  onOpenReviewComposer,
  onOpenAllReviews,
}) => {
  const { t, i18n } = useTranslation();
  const { tokens: theme } = useSalonPublicTheme();
  const reviewVariant = REVIEW_VARIANTS.has(variant) ? variant : 'cards';
  const isArrayInput = Array.isArray(data);
  const items = isArrayInput ? data : Array.isArray(data?.items) ? data.items : [];
  const preview = mode === 'preview';

  if (!visible || !data || (isArrayInput && items.length === 0)) return null;

  if (data?.isLoading || data?.error) {
    return (
      <SalonPublicStyleScope
        sectionKey="reviews"
        data-testid="salon-section-reviews"
        data-salon-template-block="reviews"
        data-variant={reviewVariant}
        sx={{ mt: 4 }}
      >
        <Typography
          component="h2"
          data-salon-text-role="title"
          variant="h5"
          sx={{ mb: 3, fontWeight: 600, color: theme.colors.gray[800] }}
        >
          {t('landing_salon_details.reviews_title')}
        </Typography>
        <Box data-salon-text-role="content">
          {data.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Typography variant="body2" color="error" sx={{ textAlign: 'center', py: 2 }}>
              {data.error}
            </Typography>
          )}
        </Box>
      </SalonPublicStyleScope>
    );
  }

  return (
    <SalonPublicStyleScope
      sectionKey="reviews"
      data-testid="salon-section-reviews"
      data-salon-template-block="reviews"
      data-variant={reviewVariant}
      sx={{
        backgroundColor: theme.colors.background.primary,
        borderRadius: 4,
        border: `1px solid ${theme.colors.border.light}`,
        p: 3,
        mt: 2,
        width: '100%',
        boxSizing: 'border-box',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StarIcon sx={{ color: theme.colors.primary[600] }} />
          <Typography
            component="h2"
            data-salon-text-role="title"
            variant="h6"
            sx={{ fontWeight: 600, color: theme.colors.gray[700] }}
          >
            {t('landing_salon_details.customer_reviews')}
          </Typography>
        </Box>
        <SalonPublicButton
          preserveLegacyStyle
          variant="contained"
          onClick={onOpenReviewComposer}
          disabled={preview}
          aria-disabled={String(preview)}
          fullWidth
          sx={{
            backgroundColor: theme.salon.primary,
            color: theme.salon.compatibilityMode ? 'white' : theme.salon.onPrimary,
            fontWeight: 600,
          }}
        >
          {data?.isAuthenticated
            ? t('landing_salon_details.give_review')
            : t('landing_salon_details.login_to_review')}
        </SalonPublicButton>
      </Box>

      <Box data-salon-text-role="content">
        {items.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, bgcolor: alpha(theme.salon.primary, 0.05), borderRadius: 2 }}>
            <Typography variant="body2" sx={{ color: theme.colors.gray[500] }}>
              {t('landing_salon_details.be_first_review')}
            </Typography>
          </Box>
        ) : (
          <>
            {Number.isFinite(Number(data?.averageRating)) ? (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, p: 1.5, bgcolor: alpha(theme.salon.primary, 0.05), borderRadius: 2 }}>
                <Rating value={Number(data.averageRating)} precision={0.5} readOnly size="small" />
                <Typography sx={{ ml: 1.5, fontWeight: 600, color: theme.colors.gray[700] }}>
                  {Number(data.averageRating).toFixed(1)}
                </Typography>
                <Typography variant="caption" sx={{ ml: 1, color: theme.colors.gray[500] }}>
                  {t('landing_salon_details.reviews_count', { count: items.length })}
                </Typography>
              </Box>
            ) : null}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {items.slice(0, 3).map((review) => (
                <Box
                  key={review.id}
                  component="article"
                  sx={{ pb: 2, borderBottom: `1px solid ${theme.colors.border.light}`, '&:last-child': { borderBottom: 0, pb: 0 } }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Typography sx={{ fontWeight: 600, color: theme.colors.gray[700] }}>
                      {review.authorName || t('landing_salon_details.client')}
                    </Typography>
                    {review.rating != null ? <Rating value={Number(review.rating)} readOnly size="small" /> : null}
                  </Box>
                  {review.comment ? (
                    <Typography variant="body2" sx={{ color: theme.colors.gray[600], lineHeight: 1.5 }}>
                      {review.comment}
                    </Typography>
                  ) : null}
                  {review.createdAt ? (
                    <Typography variant="caption" sx={{ color: theme.colors.gray[400], mt: 1, display: 'block' }}>
                      {new Date(review.createdAt).toLocaleDateString(i18n.language || 'fr-FR')}
                    </Typography>
                  ) : null}
                </Box>
              ))}
              {items.length > 3 ? (
                <SalonPublicButton
                  preserveLegacyStyle
                  variant="text"
                  size="small"
                  onClick={onOpenAllReviews}
                  disabled={preview}
                  aria-disabled={String(preview)}
                  sx={{ color: theme.colors.primary[600], fontWeight: 600 }}
                >
                  {t('landing_salon_details.show_all_reviews')}
                </SalonPublicButton>
              ) : null}
            </Box>
          </>
        )}
      </Box>
    </SalonPublicStyleScope>
  );
};

export default SalonTemplateReviews;
